import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";

import { ServiceUnavailablePage } from "@/components/ServiceProblemNotice";

import { useAuth } from "./context";

/** Only fully signed-in users get through; everyone else goes to the login page (which handles pending steps). */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, problem, refresh } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  if (status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-ink-muted" role="status">
        {t("auth.loading")}
      </div>
    );
  }
  if (status === "unavailable" && problem) {
    return <ServiceUnavailablePage problem={problem} onRetry={() => void refresh()} />;
  }
  if (status !== "active") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

/** Hides a route from users without any of the given permissions (the API refuses them anyway). */
export function RequirePermission({ anyOf, children }: { anyOf: string[]; children: ReactNode }) {
  const { canAny } = useAuth();
  if (!canAny(...anyOf)) return <Navigate to="/" replace />;
  return children;
}
